import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Settings2 } from 'lucide-react';
import { Button } from '@/components/common/Button';
import { Card } from '@/components/common/Card';
import { PageHeader } from '@/components/layout/PageHeader';
import { TextField } from '@/components/forms/Field';
import { Spinner } from '@/components/common/Spinner';
import { ErrorState } from '@/components/common/States';
import { useToast } from '@/components/feedback/ToastProvider';
import { useApiErrorHandler } from '@/hooks/useApiErrorHandler';
import { hrSettingsApi } from '@/services/api/endpoints/hr';
import { queryKeys } from '@/services/api/queryKeys';

/** Monday first, which is the order the mask is stored in. */
const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

/**
 * The school's HR configuration.
 *
 * <p>The working week is the setting that matters most and the one most easily
 * got wrong. Assuming Saturday and Sunday would be wrong for most of the schools
 * this system serves, and getting it wrong produces no error — only leave
 * balances that are quietly two days a fortnight out.
 */
export function HrSettingsPage() {
  const toast = useToast();
  const handleApiError = useApiErrorHandler();
  const queryClient = useQueryClient();

  const settings = useQuery({
    queryKey: queryKeys.hr.settings,
    queryFn: () => hrSettingsApi.get(),
  });

  const [weekend, setWeekend] = useState<boolean[]>(Array(7).fill(false));
  const [writesAttendance, setWritesAttendance] = useState(true);
  const [allowNoSupervisor, setAllowNoSupervisor] = useState(true);
  const [prefix, setPrefix] = useState('LV');

  useEffect(() => {
    if (settings.data === undefined) {
      return;
    }
    const mask = settings.data.weekendDays;
    setWeekend(DAYS.map((_, index) => mask.charAt(index) === 'Y'));
    setWritesAttendance(settings.data.leaveWritesAttendance);
    setAllowNoSupervisor(settings.data.allowLeaveWithoutSupervisor);
    setPrefix(settings.data.leaveNumberPrefix);
  }, [settings.data]);

  const save = useMutation({
    mutationFn: () =>
      hrSettingsApi.update({
        weekendDays: weekend.map((off) => (off ? 'Y' : 'N')).join(''),
        leaveWritesAttendance: writesAttendance,
        allowLeaveWithoutSupervisor: allowNoSupervisor,
        leaveNumberPrefix: prefix.trim(),
      }),
    onSuccess: () => {
      toast.success('HR settings saved');
      void queryClient.invalidateQueries({ queryKey: queryKeys.hr.settings });
    },
    onError: (error) => handleApiError(error),
  });

  if (settings.isError) {
    return <ErrorState error={settings.error} onRetry={() => void settings.refetch()} />;
  }
  if (settings.isPending) {
    return (
      <div className="flex justify-center py-16">
        <Spinner />
      </div>
    );
  }

  const everyDayOff = weekend.every((off) => off);

  return (
    <div>
      <PageHeader
        title="HR Settings"
        description="The working week and the defaults leave runs against."
        icon={<Settings2 className="h-5 w-5" aria-hidden="true" />}
      />

      <form
        onSubmit={(event) => {
          event.preventDefault();
          save.mutate();
        }}
      >
        <Card className="p-4">
          <h2 className="text-sm font-extrabold uppercase tracking-[0.05em] text-ink">
            The working week
          </h2>
          <p className="mt-1 text-[13px] text-muted">
            Tick the days the school is closed. Leave is not charged for them unless a leave type
            says otherwise, so getting this wrong quietly changes every balance.
          </p>

          <div className="mt-3 flex flex-wrap gap-2">
            {DAYS.map((day, index) => (
              <label
                key={day}
                className={`flex cursor-pointer items-center gap-2 border px-3 py-2 text-sm transition-colors ${
                  weekend[index] === true
                    ? 'border-accent bg-accent-soft text-accent-soft-fg'
                    : 'border-line text-ink hover:bg-surface'
                }`}
              >
                <input
                  type="checkbox"
                  checked={weekend[index] === true}
                  onChange={(event) =>
                    setWeekend(weekend.map((off, i) => (i === index ? event.target.checked : off)))
                  }
                  className="h-4 w-4 border-line"
                />
                {day}
              </label>
            ))}
          </div>

          {everyDayOff && (
            <p className="mt-2 text-[13px] text-[color:var(--sms-danger)]">
              At least one day has to be a working day.
            </p>
          )}
        </Card>

        <Card className="mt-3 p-4">
          <h2 className="text-sm font-extrabold uppercase tracking-[0.05em] text-ink">Leave</h2>

          <div className="mt-3 space-y-3">
            <label className="flex items-start gap-2 text-sm text-ink">
              <input
                type="checkbox"
                checked={writesAttendance}
                onChange={(event) => setWritesAttendance(event.target.checked)}
                className="mt-0.5 h-4 w-4 border-line"
              />
              <span>
                Approving leave records attendance for the days it covers
                <span className="block text-[12px] text-muted">
                  On by default, so somebody on approved leave does not appear in an absence report.
                  A day already recorded as worked is never overwritten.
                </span>
              </span>
            </label>

            <label className="flex items-start gap-2 text-sm text-ink">
              <input
                type="checkbox"
                checked={allowNoSupervisor}
                onChange={(event) => setAllowNoSupervisor(event.target.checked)}
                className="mt-0.5 h-4 w-4 border-line"
              />
              <span>
                Somebody with no supervisor may still request leave
                <span className="block text-[12px] text-muted">
                  Off means their requests are refused until a supervisor is named — tidier, but it
                  blocks people for a reason they cannot fix themselves.
                </span>
              </span>
            </label>

            <div className="max-w-[12rem]">
              <TextField
                label="Leave request prefix"
                value={prefix}
                onChange={(event) => setPrefix(event.target.value)}
                hint="Numbers read LV-2026-0001"
              />
            </div>
          </div>
        </Card>

        <div className="mt-3 flex justify-end">
          <Button type="submit" disabled={everyDayOff || save.isPending}>
            {save.isPending ? 'Saving…' : 'Save settings'}
          </Button>
        </div>
      </form>
    </div>
  );
}
